import type { ColumnDef, Getter } from "@tanstack/react-table";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SortingRule } from "react-table";
import styled from "styled-components";
import useSWR from "swr";
import { PrimaryButtonMedium } from "../../../../../../components/Buttons/Buttons";
import { DropDown } from "../../../../../../components/DropDown/DropDown";
import { IDMobile } from "../../../../../../components/IDs/IDs";
import { DropdownContainer } from "../../../../../../components/Layout/Layout";
import { Pagination } from "../../../../../../components/Pagination/Pagination";
import { Table } from "../../../../../../components/Table/Table";
import { H3 } from "../../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../../endpoints";
import { ContentWrapper } from "../../../../../../layout/portalPageLayout";
import type {
  SupportedLanguage,
  WithPagination,
} from "../../../../../../types/types";
import type {
  SupportedAssetCategoryType,
  UploadedAssetSchema,
} from "../../../../../../types/types.PIM";
import {
  defaultHandleSort,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../../../../../util/util";

const AssetsListContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
`;

export const UploadedAssetsList = ({
  setStage,
  onSelect,
  isCheckBoxRequired = false,
}: {
  setStage: Dispatch<SetStateAction<"list" | "add_files">>;
  onSelect?: (selectedAssets: string[]) => void;
  isCheckBoxRequired?: boolean;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "desc",
  });
  const [tableData, setTableData] = useState<UploadedAssetSchema[]>([]);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(10);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [should_mutate, set_should_mutate] = useState(true);

  const tableColumns = useMemo<ColumnDef<UploadedAssetSchema>[]>(
    () => [
      {
        header: t("Upload ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Asset Type"),
        accessorKey: "asset_type",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <span>{toTitleCase(getValue())}</span>
        ),
      },
      {
        header: t("Category"),
        accessorKey: "category",
        enableSorting: false,
        cell: ({
          getValue,
        }: {
          getValue: Getter<{ name: SupportedAssetCategoryType; id: string }>;
        }) => <span>{toTitleCase(getValue().name)}</span>,
      },
      {
        header: t("Language"),
        accessorKey: "language",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<SupportedLanguage> }) => (
          <span>{getValue()?.toLocaleUpperCase()}</span>
        ),
      },
      {
        header: t("# of assets"),
        enableSorting: false,
        accessorKey: "number_of_assets",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded By",
        enableSorting: false,
        accessorKey: "modified_by",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded On",
        accessorKey: "modified_at",
        cell: (cell) => cell.renderValue(),
      },
    ],
    [t]
  );

  const {
    data: previousUploadsResponse,
    mutate,
    error,
  } = useSWR<WithPagination<{ data: UploadedAssetSchema[] }>>(
    [
      endpoints.v2_tenants_tenant_id_pim_assets_uploads(tenant_id),
      useMemo(
        () => ({
          params: {
            offset,
            limit: perPage,
            ...(sortingRules.sortBy ? { sort_by: sortingRules.sortBy } : {}),
            order_by: sortingRules.orderBy ?? "desc",
          },
        }),
        [offset, perPage, sortingRules.orderBy, sortingRules.sortBy]
      ),
    ],
    { revalidateOnMount: true }
  );

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = (perPage: number) => {
    setPerPage(perPage);
    if (perPage > offset) {
      setOffset(0);
    }
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const isLoading = !previousUploadsResponse && !error;

  useEffect(() => {
    const handlePreviousUpdatesData = ({
      data,
      pagination,
    }: WithPagination<{ data: UploadedAssetSchema[] }>) => {
      setTableData(
        data.map((data) => ({
          ...data,
          modified_at: formatDateTime(data.modified_at),
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (previousUploadsResponse) {
      handlePreviousUpdatesData(previousUploadsResponse);
    }
  }, [perPage, previousUploadsResponse]);

  useEffect(() => {
    if (should_mutate && !!mutate) {
      mutate();
      set_should_mutate(false);
    }
  }, [mutate, should_mutate]);

  const handleSelect = useCallback(
    (selectedRows: string[] | "all") => {
      if (onSelect && selectedRows !== "all") {
        onSelect(selectedRows);
      }
    },
    [onSelect]
  );

  return (
    <AssetsListContainer>
      {!isCheckBoxRequired && (
        <PrimaryButtonMedium
          style={{ maxWidth: "168px" }}
          onClick={() => setStage("add_files")}
        >
          {t("Start a new upload")}
        </PrimaryButtonMedium>
      )}
      <H3 style={{ marginTop: "38px" }}>{t("Previous Uploads")}</H3>
      <div>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
        <ContentWrapper>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={isLoading}
            error={error}
            handleSort={handleSort}
            lastChildleftAlign={true}
            handleSelectRows={isCheckBoxRequired ? handleSelect : undefined}
          />
          <Pagination
            pagination={tablePagination}
            offset={offset}
            handlePageClick={changePage}
          />
        </ContentWrapper>
      </div>
    </AssetsListContainer>
  );
};
